import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { TranslateService } from "@ngx-translate/core";
import { WebViewerInstance } from "@pdftron/webviewer";
import { environment } from "environments/environment";
import { DownloadListComponent } from "../download-list/download-list.component";
import { WatermarkDMS } from "../../../shared/types";
import { DocumentData } from "../document-data.type";

/**
 * Show a dialog box asking user to confirm whether they want to download document.
 * User can also choose to include 'Annotation' and/or 'Watermark' into the document.
 */
export function promptDownloadDocDialog(instance: WebViewerInstance, documentData: DocumentData, dialog: MatDialog, translate: TranslateService) {
        /** Config dialog box */
        let dialogConfig: {
            title: string,
            list: { id: number, selected: boolean, name: string }[],
        } = {
            title: "",
            list: []
        };;

        translate.get('Download-dialog').subscribe((titleDialog: string) => {
            dialogConfig.title = titleDialog;
        });
        translate.get('Annotation').subscribe((AnnotationCheckboxLabel: string) => {
            dialogConfig.list.push({ id: 1, selected: false, name: AnnotationCheckboxLabel });
        });
        if (documentData.watermark) {
            translate.get("Watermark").subscribe((watermarkCheckboxLabel: string) => {
                dialogConfig.list.push({ id: 2, selected: false, name: watermarkCheckboxLabel });
            });
        }

        /** Show Print Confirmation Dialog Box */
        const dialogRef = dialog.open(DownloadListComponent, {
           data: dialogConfig
        });

        /** Retrieve user's checked/unchecked checkbox options */
        let checkboxStatus: typeof dialogConfig.list;
        const dialogSubmit = dialogRef.componentInstance.submitData.subscribe(
            (_checkboxStatus: typeof dialogConfig.list) => {
                checkboxStatus = _checkboxStatus;
                dialogSubmit.unsubscribe();
            }
        );

        /**  Download doc if user clicked 'Download' or do nothing if 'Cancel' */
        dialogRef.afterClosed().subscribe((confirmPrint: boolean) => {
            if (confirmPrint) {
                setDownload(
                    instance,
                    getFileName(documentData),
                    checkboxStatus[0].selected,
                    (checkboxStatus[1]?.selected) ? documentData.watermark : undefined
                );
            }
        });
}

function setDownload(instance: WebViewerInstance, fileName: string, includeAnnotations: boolean, watermark?: WatermarkDMS) {
    let options = {
        filename: fileName,
        includeAnnotations: includeAnnotations,
    };
    if (watermark) {
        setWatermarkDownload(instance, watermark).then(a => {
           instance?.UI.downloadPdf(options).then(b => {
              clearWatermark(instance, watermark);
           });
        });
     } else {
        instance?.UI.downloadPdf(options);
    }
}

function getFileName(documentData) {
    const lastIndexName = documentData.fileName.lastIndexOf('.');
    const newFilename = documentData.fileName.substring(0, lastIndexName);
    const subTypeFileName = documentData.fileName.split('.');
    const typeFile = subTypeFileName[subTypeFileName.length - 1];
    return decodeURIComponent(newFilename) + '.' + typeFile;
}

async function setWatermarkDownload(ins: WebViewerInstance, dataWaterMark: WatermarkDMS) {
    if (dataWaterMark && dataWaterMark != null) {
       const { PDFNet } = ins?.Core;
       await PDFNet.initialize();

       const totalPage = ins?.Core.documentViewer.getDocument().getPageCount();
       const doc = await ins?.Core.documentViewer.getDocument().getPDFDoc();
       // Run PDFNet methods with memory management
       PDFNet.runWithCleanup(async () => {

          // lock the document before a write operation
          // runWithCleanup will auto unlock when complete
          const s = await PDFNet.Stamper.create(PDFNet.Stamper.SizeType.e_font_size, dataWaterMark.fontSize, 0);

          await s.setAlignment(PDFNet.Stamper.HorizontalAlignment.e_horizontal_center, PDFNet.Stamper.VerticalAlignment.e_vertical_center);
          const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(dataWaterMark.color);
          const r = parseInt(result[1], 16);
          const g = parseInt(result[2], 16);
          const b = parseInt(result[3], 16);
          const redColorPt = await PDFNet.ColorPt.init(r / 255, g / 255, b / 255, 0);
          await s.setFontColor(redColorPt);
          await s.setTextAlignment(PDFNet.Stamper.TextAlignment.e_align_center);
          await s.setAsBackground(false);
          await s.setOpacity(dataWaterMark.opacity / 100);
          await s.setRotation(-45);
          const pgSet = await PDFNet.PageSet.createRange(1, totalPage);
          await s.stampText(doc, dataWaterMark.text, pgSet);
       }, environment.pdfTronKey.toString() /* runWithCleanup requires PDFTron license key here */
       );
    }
 }

 async function clearWatermark(ins: WebViewerInstance, dataWaterMark: WatermarkDMS) {
    if (dataWaterMark && dataWaterMark != null) {
       const { PDFNet } = ins?.Core;
       await PDFNet.initialize();

       const totalPage = ins?.Core.documentViewer.getDocument().getPageCount();
       const doc = await ins?.Core.documentViewer.getDocument().getPDFDoc();
       PDFNet.runWithCleanup(async () => {

          // lock the document before a write operation
          // runWithCleanup will auto unlock when complete
          const allPgSet = await PDFNet.PageSet.createFilteredRange(1, totalPage, PDFNet.PageSet.Filter.e_all);
          await PDFNet.Stamper.deleteStamps(doc, allPgSet);
       }, environment.pdfTronKey.toString() /* runWithCleanup() requires PDFTron license key here */
       );
    }
 }
